// Role management utilities
export const ROLES = {
  ADMIN: 'Admin',
  USER: 'User',
  ISSUER: 'Issuer',
  ANALYST: 'Analyst',
  EXTERNAL_USER: 'EXTERNAL_USER',
};

const ADMIN_ROLES = ['admin', 'super_admin'];

// Parse JWT token to get user information
export function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(window.atob(base64));
  } catch (e) {
    console.error("Error parsing JWT token:", e);
    return null;
  }
}

export function normalizeRoles(roles) {
  if (!roles) return [];
  if (Array.isArray(roles)) return roles;
  return [...roles];
}

export function isAdminRole(roles = []) {
  return normalizeRoles(roles).some(
    (role) => role && ADMIN_ROLES.includes(String(role).toLowerCase())
  );
}

export function isExternalUserRole(roles = []) {
  const normalized = normalizeRoles(roles);
  if (normalized.length === 0) return true;
  return normalized.includes(ROLES.EXTERNAL_USER);
}

export function isExternalUser(userData) {
  if (!userData?.id) return false;
  return isExternalUserRole(userData.roles) && !isAdminRole(userData.roles);
}

export function getPostLoginPath(roles = []) {
  if (isAdminRole(roles)) return '/admin';
  if (isExternalUserRole(roles)) return '/my-portfolio';
  return '/d/dash';
}

// In cookie-based auth we prefer roles from backend/user context.
// This function is kept for backwards compatibility; you can later
// wire it to UserContext instead of reading from localStorage.
export function getUserRoles() {
  const token = localStorage.getItem("authToken");
  if (!token) return [];

  const payload = parseJwt(token);
  return payload?.roles || [];
}

// Check if user has a specific role (case-insensitive)
export function hasRole(role) {
  const userRoles = getUserRoles();
  return userRoles.some(r => r.toLowerCase() === role.toLowerCase());
}

// Check if user has any of the specified roles
export function hasAnyRole(roles) {
  const userRoles = getUserRoles();
  return roles.some(role =>
    userRoles.some(userRole => userRole.toLowerCase() === role.toLowerCase())
  );
}

// Check if user has all of the specified roles
export function hasAllRoles(roles) {
  const userRoles = getUserRoles();
  return roles.every(role =>
    userRoles.some(userRole => userRole.toLowerCase() === role.toLowerCase())
  );
}

// Check if user is admin
export function isAdmin() {
  return hasRole(ROLES.ADMIN);
}

// Check if user is regular user (not admin)
export function isRegularUser() {
  const userRoles = getUserRoles();
  return userRoles.length > 0 && !userRoles.some(role =>
    role.toLowerCase() === ROLES.ADMIN.toLowerCase()
  );
}

// Get user email from token
export function getUserEmail() {
  const token = localStorage.getItem("authToken");
  if (!token) return null;

  const payload = parseJwt(token);
  return payload?.sub || payload?.email || null;
}

// Get user ID from token
export function getUserId() {
  const token = localStorage.getItem("authToken");
  if (!token) return null;

  const payload = parseJwt(token);
  return payload?.userId || payload?.sub || null;
}

// Safe logout helper for React components, compatible with cookie-based auth.
export function clearAuthData(navigate, clearUserData) {
  try {
    localStorage.removeItem("authToken");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("twoFAHandled");
    localStorage.removeItem("2faPopupShown");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userName");
    localStorage.removeItem("userPhone");
    sessionStorage.removeItem("userPolicies");

    if (typeof clearUserData === "function") {
      clearUserData();
    }
    if (typeof navigate === "function") {
      navigate("/home", { replace: true });
    }
  } catch (e) {
    console.error("Error clearing auth data:", e);
  }
}
